<template>
  <b-overlay :show="processing">
    <b-card body-class="p-0">
      <b-table id="hours-tracked" :items="users" :fields="fields" striped responsive class="hours-tracked mb-0"
        selectable show-empty @row-clicked="row => showDetails(row)">
        <template #head()="data">
          <div class="d-flex text-capitalize">
            {{ data.label }}
            <table-header-sort :sort-label="data.field.sortLabel" :sort-options="sortOptions"
              @update:sort-options="onSortChanged" />
          </div>
        </template>
        <template #cell(name)="row">
          <div class="d-flex align-items-center" style="width: 68vw;">
            <!-- Accordion Icon -->
            <feather-icon v-if="row.detailsShowing" size="20" icon="ChevronUpIcon" />
            <feather-icon v-else size="20" icon="ChevronDownIcon" />

            <b-avatar :src="getAvatar(row.item.avatar)" size="30" badge
              :badge-variant="row.item.isOnline ? 'success' : 'secondary'" :text="getInitials(row.item.name)" class="mr-1"
              variant="light-primary" />

            <span>{{ IS_MOBILE() ? FORMAT_NAME(row.item.name) : row.item.name }}</span>
            <!-- Tags -->
            <div v-if="row.item.tags && row.item.tags.length" class="ml-2">
              <span v-for="tag in (row.item.tags.slice(0, 3))" :key="tag.id" class="mr-1">
                <tag-badge :name="tag.name" :color="tag.color" :description="tag.description || ''" size="small" />
              </span>
              <span v-if="row.item.tags.length > 3">
                <b-badge variant="primary" class="ml-1">+{{ row.item.tags.length - 3 }} {{ $t('more')
                }}</b-badge>
              </span>
            </div>
          </div>
        </template>
        <template #cell(dailyActivities)="row">
          <b>{{ CONVERT_HM(getTotalTimes(row.item)) }}</b>
        </template>
        <template #row-details="row">
          <b-card v-if="row.item.dailyActivities.data.length" body-class="m-0 p-0 p-md-1">
            <hours-tracked-chart v-if="showChart" :activities="row.item.dailyActivities.data" />
            <h5 class="my-1">
              {{ $t('Details') }}
            </h5>
            <b-row
              v-for="(dailyActivity, index) in showingMore ? getDurationCalculation(row.item).activities : getDurationCalculation(row.item).activities.slice(0, 5)"
              :key="index" class="p-1 border">
              <b-col sm="3" class="text-link" @click="SHOW_RECORD(dailyActivity.date, row.item.uuid)">
                {{ dailyActivity.date }}
              </b-col>
              <b-col sm="3">
                {{ CONVERT_HM(dailyActivity.duration) }}
              </b-col>
              <b-col sm="6">
                <b-progress style="border-radius: 0;" class="d-flex" height="1.3rem" :max="100"
                  :set="percentInfo = getTimeDuration(dailyActivity, getDurationCalculation(row.item).maxDuration)">
                  <b-progress-bar v-b-tooltip.hover.top
                    :title="((1 - dailyActivity.idlePercent) > 0.3 && dailyActivity.duration > 1300) ? 'Working' : `Working - ${Math.round((1 - dailyActivity.idlePercent) * 100, 0)} %`"
                    :value="percentInfo.activePercent" variant="success" style="border-radius: 0;">
                    <span
                      v-if="((1 - dailyActivity.idlePercent) > 0.3 && dailyActivity.duration > 1300) || dailyActivity.duration > 3600">
                      {{ Math.round((1 - dailyActivity.idlePercent) * 100, 0) }} %
                    </span>
                  </b-progress-bar>
                  <b-progress-bar v-b-tooltip.hover.top
                    :title="((dailyActivity.idlePercent) > 0.3 && dailyActivity.duration > 1300) ? 'Idle' : `Idle - ${Math.round((dailyActivity.idlePercent) * 100, 0)} %`"
                    :value="percentInfo.idlePercent" class="bg-idle" style="border-radius: 0;">
                    <span
                      v-if="(dailyActivity.idlePercent > 0.3 && dailyActivity.duration > 1300) || dailyActivity.duration > 3600"
                      class="text-primary">
                      {{ Math.round(dailyActivity.idlePercent * 100, 0) }} %
                    </span>
                  </b-progress-bar>
                </b-progress>
              </b-col>
            </b-row>
            <div v-if="getDurationCalculation(row.item).activities.length > 5" class="w-100 my-1"
              :class="IS_MOBILE() ? 'ml-1' : 'text-center'">
              <b-button variant="outline-primary" @click="showingMore = !showingMore">
                {{ showingMore ? $t('Show Less') : $t('Show More') }}
                <feather-icon :icon="showingMore ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
              </b-button>
            </div>
          </b-card>
          <b-card v-else bg-variant="light-danger" class="text-center">
            <span class="text-danger p-1">
              {{ $t('reports.not-tracked') }}
            </span>
          </b-card>
        </template>
      </b-table>
    </b-card>

    <!-- Pagination -->
    <div class="centralize w-100">
      <b-pagination v-if="totalRows > perPage" v-model="currentPage" aria-controls="hours-tracked"
        :total-rows="totalRows" :per-page="perPage" @input="getDailyActivityRecords" />
    </div>
  </b-overlay>
</template>
<script>
import {
  BCard, BProgress, BProgressBar, BButton, VBTooltip,
  BCol, VBToggle, BTable, BAvatar, BOverlay, BPagination,
} from 'bootstrap-vue'
import useApollo from '@/plugins/graphql/useApollo'
import { setComponentReady } from '@/views/tour/tour'
import HoursTrackedChart from './HoursTrackedChart.vue'
import TagBadge from '@/views/Settings/UserTags/Components/TagBadge.vue'
import TableHeaderSort from '@/views/common/components/TableHeaderSort.vue'
import useFilters from '@/views/common/components/Filters/useFilters'
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    BCard,
    BProgress,
    BProgressBar,
    BCol,
    BButton,
    BTable,
    BAvatar,
    BOverlay,
    BPagination,
    HoursTrackedChart,
    TableHeaderSort,
    TagBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
    'b-toggle': VBToggle,
  },
  props: {
    showChart: {
      type: Boolean,
      default: () => true,
    },
    startDate: {
      type: [Object, Date, String],
      default: null,
    },
    endDate: {
      type: [Object, Date, String],
      default: null,
    },
    userId: {
      type: [Object, String, Array],
      default: null,
    },
    timezone: {
      type: [Object, String],
      default: null,
    },
    sortOptions: {
      type: Object,
      default: () => ({ sortBy: 'HOURS_TRACKED', sortDirection: 'DESC' }),
    },
  },

  data() {
    return {
      processing: false,
      maxDuration: 0,
      users: [],
      fields: [
        { key: 'name', label: this.$t('Name'), sortLabel: 'NAME' },
        { key: 'dailyActivities', label: this.$t('Total Worked'), sortLabel: 'HOURS_TRACKED' },
      ],
      showingMore: false,
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
    }
  },
  computed: {
    ...mapGetters('project', ['GET_USERS']),
    activeFilters() {
      const { getActiveFilters } = useFilters()
      return getActiveFilters.value
    }
  },
  watch: {
    startDate: {
      handler() {
        this.getDailyActivityRecords()
      },
      immediate: true,
    },
    endDate: {
      handler() {
        this.getDailyActivityRecords()
      },
      immediate: true,
    },
    userId: {
      handler() {
        this.getDailyActivityRecords()
      },
      immediate: true,
    },
    timezone: {
      handler() {
        this.getDailyActivityRecords()
      },
      immediate: true,
    },
    sortOptions: {
      handler(val) {
        this.currentPage = 1
        this.getDailyActivityRecords()
      },
      immediate: true,
    },
    activeFilters: {
      handler() {
        this.currentPage = 1
        this.getDailyActivityRecords()
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions('project', ['updateUsers', 'updateHasNextUsersPage']),
    onSortChanged(params) {
      this.currentPage = 1
      this.$emit('update:sort-options', params)
    },
    showDetails(row) {
      if (row._showDetails) {
        this.$set(row, '_showDetails', false)
      } else {
        this.users.forEach(item => {
          this.$set(item, '_showDetails', false)
        })

        this.$nextTick(() => {
          this.$set(row, '_showDetails', true)
        })
      }
    },
    getDailyActivityRecords: _.debounce(function () {
      this.processing = true
      const startTime = `${this.startDate}`
      const endTime = `${this.endDate}`
      useApollo.activities.getAllDailyActivities({
        startTime,
        endTime,
        projectId: this.$store.state.project.selectedProject,
        userId: this.userId,
        first: this.perPage,
        page: this.currentPage,
        sortBy: this.sortOptions.sortBy,
        sortDirection: this.sortOptions.sortDirection,
        timezone: this.timezone,
        tags: this.activeFilters.tags.map(tag => tag.name),
        activities: this.activeFilters.activities,
        ...(this.activeFilters.isOnline !== null && { onlineStatus: this.activeFilters.isOnline }),
      }).then(response => {
        const res = response.data.users
        this.users = res.data
        this.totalRows = res.total
        this.updateUsers(this.users.map(({ name, uuid, avatar, tags, isOnline }) => ({
          name,
          text: name,
          value: uuid,
          avatar,
          tags,
          isOnline,
        }))).then(() => {
          if (this.GET_USERS.length === res.total) this.updateHasNextUsersPage(false)
        })
        setComponentReady()
      }).catch(error => {
        this.showErrorMessage(error)
        console.error(error)
      }).finally(() => { this.processing = false })
    }),
    getTimeDuration(dailyActivity, maxDuration) {
      const totalPercent = (dailyActivity.duration / maxDuration) * 100
      const idlePercent = dailyActivity.idlePercent * totalPercent
      return {
        activePercent: totalPercent - idlePercent,
        idlePercent,
      }
    },
    getDurationCalculation(item) {
      const activities = item.dailyActivities.data
      const maxDuration = Math.max(...activities.map(activity => activity.duration))
      return {
        maxDuration,
        activities,
      }
    },
    getTotalTimes(item) {
      const activities = item.dailyActivities.data
      return activities.reduce((p, c) => p + c.duration, 0)
    },
  },
}
</script>
<style lang="scss" scoped>
.bg-idle {
  background-color: #c1c7cf !important;
}

.progress {
  background-color: transparent !important;
}
</style>

<style lang="scss">
@media screen and (max-width: 768px) {
  .hours-tracked {
    td {
      padding: 10px 4px;
    }
  }
}
</style>
